import React, { useEffect, useState } from 'react';
import { MainBox, stripeAccountManagementSx } from '../styles';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../Infrastructure/config';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { Box, CircularProgress, Typography } from '@mui/material';
import { USER_LOGS_CLIENT } from '../../../Infrastructure/Constants/logsConstant';
import { useSelector } from 'react-redux';
import { safeClientLogEvent } from '../../../Infrastructure/Utilities/safeClientLogEvent';
import { ConnectAccountManagement, ConnectAccountOnboarding, ConnectComponentsProvider, ConnectPayments, ConnectPayouts } from '@stripe/react-connect-js';

const publishableKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;

export const StripeAccountManagement = ({ accountId, error, setError, loading, setLoading, userId }) => {
  const ip = useSelector((state) => state.ip.address)
  const [isInitializing, setIsInitializing] = useState(true);
  const page = useSelector((state)=> state.route.currentRoute)
  const [connectInstance, setConnectInstance] = useState(null);

  useEffect(() => {
    const getStripeAccSecretId = async () => {
      try {
        setLoading(true);
        const getSecret = httpsCallable(functions, "getstripeaccountsecret");
        const res = await getSecret({ accountId, ip, page });

        if (res?.data?.clientSecret) {
          await initializeStripe(res.data.clientSecret);
        } else {
          throw new Error("Failed to retrieve the Stripe client secret.");
        }
      } catch (error) {
        await safeClientLogEvent({
          ...USER_LOGS_CLIENT.STRIPE_SECRET_FETCH_FAILED,
          uid: userId || null,
          source: "stripeAccountManagement.jsx",
          emailInfo: null,
          metadata: { accountId },
          error: error?.message || null
        });
        setError("Unable to get the Stripe secret. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    const initializeStripe = async (clientSecret) => {
      if (!publishableKey) {
        setError("Publishable key is not configured. Please contact support.");
        return;
      }

      try {
        const instance = await loadConnectAndInitialize({
          publishableKey,
          fetchClientSecret: async () => clientSecret,
        });
        setConnectInstance(instance);
      } catch (error) {
        await safeClientLogEvent({
          ...USER_LOGS_CLIENT.STRIPE_INITIALIZATION_FAILED,
          uid: userId || null,
          source: "stripeAccountManagement.jsx",
          emailInfo: null,
          metadata: { accountId },
          error: error?.message || null
        });
        setError("Error initializing Stripe. Please try again later.");
      } finally {
        setIsInitializing(false);
      }
    };

    if (accountId) {
      getStripeAccSecretId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  if (loading || isInitializing ) {
    return (
      <Box sx={stripeAccountManagementSx.root}>
        <CircularProgress />
        <Typography variant="body1" color="initial">Please wait...</Typography>
      </Box>
    );
  }
  if (!connectInstance) {
    return <Typography color="error">Failed to initialize Stripe. Please try again later.</Typography>;
  }
  return (
    <MainBox width={"full"}>
      <ConnectComponentsProvider connectInstance={connectInstance}>
        <ConnectPayments />
        <Box my={3}></Box>
        <ConnectPayouts />
        <Box my={3}></Box>
        <ConnectAccountManagement />
        <Box my={3}></Box>
        <ConnectAccountOnboarding />
        <Box my={3}></Box>
      </ConnectComponentsProvider>
    </MainBox>
  );
};
